import { v4 as uuidv4 } from 'uuid'
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import { storage } from '@/lib/storage'
import { PosModel } from '@/types'

import { useCart } from './CartContext'

const STORAGE_KEY = 'iws-pos:held'

interface HeldOrdersContextValue {
  held: PosModel.HeldOrder[]
  /** Parks the current cart and empties it. Returns false when there is nothing to hold. */
  hold: (label?: string) => boolean
  /** Swaps a held order into the cart; a non-empty cart is held first so nothing is lost. */
  resume: (id: string) => void
  discard: (id: string) => void
}

const HeldOrdersContext = createContext<HeldOrdersContextValue | null>(null)

export const HeldOrdersProvider = ({ children }: { children: ReactNode }) => {
  const cart = useCart()
  const [held, setHeld] = useState<PosModel.HeldOrder[]>(() => storage.read(STORAGE_KEY, []))

  useEffect(() => storage.write(STORAGE_KEY, held), [held])

  const value = useMemo<HeldOrdersContextValue>(() => {
    const snapshot = (label?: string): PosModel.HeldOrder => ({
      id: uuidv4(),
      label: label || cart.customer?.name || `Walk-in #${held.length + 1}`,
      held_at: new Date().toISOString(),
      lines: cart.lines,
      customer: cart.customer,
      discount: cart.discount,
    })

    return {
      held,
      hold: (label) => {
        if (!cart.lines.length) return false
        setHeld((prev) => [snapshot(label), ...prev])
        cart.clear()
        return true
      },
      resume: (id) => {
        const order = held.find((h) => h.id === id)
        if (!order) return
        setHeld((prev) => [...(cart.lines.length ? [snapshot()] : []), ...prev.filter((h) => h.id !== id)])
        cart.load({ lines: order.lines, customer: order.customer, discount: order.discount })
      },
      discard: (id) => setHeld((prev) => prev.filter((h) => h.id !== id)),
    }
  }, [held, cart])

  return <HeldOrdersContext.Provider value={value}>{children}</HeldOrdersContext.Provider>
}

export const useHeldOrders = () => {
  const ctx = useContext(HeldOrdersContext)
  if (!ctx) throw new Error('useHeldOrders must be used inside HeldOrdersProvider')
  return ctx
}
